import {
  filterTypesEnum,
  type FilterHierarchyStatusType,
} from "@/types/common/filters";

const farIcon = (name: string, color: string) => ({ name, type: "far", color });

const clockInfoIcon = farIcon("clock", "info");
const repeatActionIcon = farIcon("arrows-repeat", "action");

const child = (
  label: string,
  field: string,
  icon: ReturnType<typeof farIcon>,
) => ({
  label,
  field,
  value: false,
  icon,
});

const statusParent = (
  label: string,
  field: string,
  children: ReturnType<typeof child>[],
) => ({ label, field, value: false, children });

export const buildHierarchyStatusString = (
  data: FilterHierarchyStatusType,
): string => {
  return data.groups
    .flatMap((group) =>
      group.status
        .map((parent) => {
          const selectedChildren =
            parent.children
              ?.filter((child) => child.value)
              .map((child) => child.field) ?? [];

          if (selectedChildren.length === 0) return null;

          return `${parent.field
            .replace(/-node$/i, "")
            .replaceAll("-", "_")
            .toUpperCase()}:${selectedChildren.join(",")}`;
        })
        .filter(Boolean),
    )
    .join("|");
};

export const parseHierarchyStatusString = (
  status: string,
): Record<string, string[]> => {
  if (!status.trim()) return {};

  return status.split("|").reduce<Record<string, string[]>>((acc, item) => {
    const [parentKey, childrenRaw] = item.split(":");

    if (!parentKey) return acc;

    const key = parentKey.trim();

    const values = childrenRaw
      ? childrenRaw
          .split(",")
          .map((value) => value.trim())
          .filter(Boolean)
      : [];

    acc[key] = [...(acc[key] ?? []), ...values];

    return acc;
  }, {});
};

export const getHierarchyParentKey = (label: string): string => {
  const map: Record<string, string> = {
    "Formulario interno": "FORM",
    "Formulario externo": "FORM",
    Revisión: "REVISION",
    "Solicitud de plazo": "REVISION",
    Pago: "PAYMENT",
    Firma: "SIGN_DOCUMENT",
    Resolución: "RESOLUTION",
    "Acciones del sistema": "GENERAL",
    "Asociación de usuario": "ASSOCIATE_USER",
    "Confirmar asociación": "CONFIRM_ASSOCIATE_USER",
  };

  return map[label] ?? label.replace(/\s+/g, "_").toUpperCase();
};

export const buildHierarchyStatusFilter = (initialStatus: string) => {
  const parsedStatus = parseHierarchyStatusString(initialStatus);

  const groups = hierarchyStatusTemplate.groups.map((group) => ({
    ...group,
    status: group.status.map((parent) => {
      const parentKey = getHierarchyParentKey(parent.label);

      let selectedChildren = parsedStatus[parentKey] ?? [];

      if (parentKey === "FORM") {
        if (parent.label === "Formulario externo") {
          selectedChildren = selectedChildren.filter((s) =>
            s.startsWith("EXTERNAL"),
          );
        }

        if (parent.label === "Formulario interno") {
          selectedChildren = selectedChildren.filter(
            (s) => !s.startsWith("EXTERNAL"),
          );
        }
      }

      const children =
        parent.children?.map((child) => ({
          ...child,
          value: selectedChildren.includes(child.field),
        })) ?? [];

      return {
        ...parent,
        value: children.every((child) => child.value),
        children,
      };
    }),
  }));

  const isUpdated = initialStatus.trim() !== "";

  return {
    id: 2,
    type: filterTypesEnum.hierarchyStatus,
    label: "Estado de solicitud",
    field: "status",
    result: initialStatus,
    isUpdated,
    groups,
  };
};

export const hierarchyStatusTemplate = {
  id: 2,
  type: filterTypesEnum.hierarchyStatus,
  label: "Estado de solicitud",
  result: "",
  field: "status",
  isUpdated: false,
  groups: [
    {
      label: "Interno",
      status: [
        statusParent("Formulario interno", "form-node", [
          child("Pendiente", "PENDING", clockInfoIcon),
          child("En proceso", "IN_PROCESS", repeatActionIcon),
        ]),
        statusParent("Formulario externo", "form-node", [
          child(
            "Pendiente de ingreso",
            "EXTERNAL_FORM_PENDING_ENTRY",
            clockInfoIcon,
          ),
          child(
            "Ingresado",
            "EXTERNAL_FORM_RECEIVED",
            farIcon("envelope-dot", "action"),
          ),
        ]),
        statusParent("Revisión", "revision-node", [
          child("Pendiente", "PENDING", clockInfoIcon),
          child("En revisión", "UNDER_REVIEW", repeatActionIcon),
          child(
            "Esperando visto bueno",
            "IN_LEADER_REVIEW",
            farIcon("clock", "warning"),
          ),
          child(
            "Observaciones enviadas",
            "WITH_CORRECTIONS",
            farIcon("send", "warning"),
          ),
          child(
            "En proceso de corrección",
            "REVIEW_CORRECTION_IN_PROGRESS",
            farIcon("file-edit", "action"),
          ),
          child(
            "Correcciones recibidas",
            "REVIEW_CORRECTIONS_RECEIVED",
            farIcon("envelope", "warning"),
          ),
        ]),
        statusParent("Solicitud de plazo", "revision-node", [
          child(
            "Solicitud de prorroga",
            "EXTENSION_REQUEST",
            farIcon("timer", "warning"),
          ),
          child(
            "Solicitud de suspensión",
            "SUSPENSION_REQUEST",
            farIcon("circle-stop", "warning"),
          ),
          child("Suspendida", "SUSPENDED", farIcon("circle-stop", "neutral")),
        ]),
        statusParent("Pago", "payment-node", [
          child(
            "Pendiente de pago",
            "PAYMENT_PENDING",
            farIcon("dollar", "info"),
          ),
        ]),
        statusParent("Firma", "sign-document-node", [
          child("Pendiente de firma", "SIGNATURE_PENDING", clockInfoIcon),
          child(
            "En proceso de firma",
            "SIGNATURE_IN_PROGRESS",
            repeatActionIcon,
          ),
        ]),
        statusParent("Acciones del sistema", "general-node", [
          child("En proceso", "PREPARING_PROCESS", repeatActionIcon),
        ]),
        statusParent("Asociación de usuario", "associate-user-node", [
          child("Pendiente", "PENDING", clockInfoIcon),
          child("En proceso", "IN_PROCESS", farIcon("loader", "action")),
          child("Ingresado", "SUBMITTED", farIcon("user-check", "success")),
        ]),
        statusParent("Confirmar asociación", "confirm-associate-user-node", [
          child("Pendiente", "PENDING", clockInfoIcon),
          child("En proceso", "IN_PROCESS", repeatActionIcon),
          child("Ingresado", "SUBMITTED", farIcon("check", "success")),
        ]),
        statusParent("Resolución", "resolution-node", [
          child("Aprobado", "APPROVED", farIcon("check", "success")),
          child("Denegado", "REJECTED", farIcon("times", "danger")),
          child("Desistido", "CANCELED", farIcon("minus", "neutral")),
        ]),
      ],
    },
  ],
};
